import { useEffect, useRef, useState, useSyncExternalStore, type ReactElement } from "react";
import { PARTY_SIZE } from "odyssey-shared";
import { Button, Glyph, KIT_METRICS, HudFrame, logical } from "../../ui/kit/index.ts";
import { barFill } from "../../ui/kit/bar.ts";
import { FRAMES } from "odyssey-shared";
import { slotRect } from "../hud/area-slots.ts";
import type { HudArea } from "../hud/slots.ts";
import type { MaintenanceWarnings } from "../hud/maintenance-warning.ts";
import type { HudSource } from "../hud/store.ts";
import { say } from "../../words.ts";
import { promptFlagView } from "./prompt-flag.ts";
import { flagRefusalLine } from "../hud/flag-toggle.ts";
import { switchAndAccept, type SwitchEnd } from "./switch-accept.ts";
import type { PartyPrompt, PartySource } from "./store.ts";

/**
 * The invitation prompt (ADR-101 decision 5 and its note of 2026-10-15; the design's `Invite` card in
 * `ui_kits/surfaces/NoticeSurfaces.jsx`, a visual reference): at the top centre, in the maintenance strip's place (below it while it shows) and
 * clear of the character, a HUD frame holding the party icon and "Party invitation", the inviter's name with how many members the
 * party seats, a timer bar that counts the offer down, and Decline and Accept. One invitation shows at a time; the rest
 * are queued, said as "n more waiting".
 *
 * Beneath the timer bar it says what the party's flag asks of the player (ADR-076's amendment of 2026-10-08, E3): a
 * party whose flag the player does not hold reads "To accept, you'll need to flag" or "unflag" and its Accept reads
 * "Switch and accept", which sends the switch and then the accept once the preference reports it. A Flagged party below
 * the rules' level offers Decline only; a block the flag panel also shows holds the Accept; a flag the page was not
 * told (unknown) says nothing and offers Accept.
 *
 * It is not modal. It never takes keyboard focus (its buttons are in the tab order like every HUD control, and a
 * press on them keeps focus where it was), it holds no keyboard layer and blocks no movement or ability, and it
 * is gone the moment the player is in combat (the store shows none then).
 */

/**
 * How often the timer bar redraws, in milliseconds: well under one of its logical pixels at the offer's length.
 */
const TIMER_REDRAW_MS = 200;

/**
 * The timer bar's width in logical pixels, the design's 160.
 */
export const PROMPT_BAR_WIDTH = 160;

/**
 * The whole seconds left on an offer, rounded up.
 *
 * @param prompt The invitation.
 * @param now The page's clock.
 * @returns Seconds, at least 0.
 */
export function secondsLeft(prompt: PartyPrompt, now: number): number {
  return Math.max(0, Math.ceil((prompt.deadline - now) / 1000));
}

/**
 * The timer bar: a track whose fill is the share of the offer still open, cropped to whole logical pixels as every bar
 * is, named for assistive technology by the seconds left. It takes no pointer events.
 *
 * @param props The `prompt` and the page's clock reading `now`.
 * @returns The bar.
 */
function TimerBar({ prompt, now }: { readonly prompt: PartyPrompt; readonly now: number }): ReactElement {
  const metrics = KIT_METRICS.bar;
  const inner = PROMPT_BAR_WIDTH - metrics.insets;
  const left = Math.max(0, prompt.deadline - now);
  const fill = barFill(left, prompt.total, inner);
  return (
    <div className="ot-progress" data-kit="progress" style={{ width: logical(PROMPT_BAR_WIDTH) }}>
      <div role="progressbar" aria-label={say("party.prompt.timer", { seconds: String(secondsLeft(prompt, now)) })} aria-valuemin={0} aria-valuemax={Math.ceil(prompt.total / 1000)} aria-valuenow={secondsLeft(prompt, now)} className="ot-progress__track ot-ui-frame-bar-track" data-kit="progress-track" data-party="timer" style={{ height: logical(metrics.track) }}>
        <div className="ot-progress__crop" data-kit="progress-crop" data-fill={fill} style={{ width: logical(fill), height: logical(metrics.fill) }}>
          <div className="ot-progress__fill ot-ui-frame-bar-fill" style={{ width: logical(inner), height: logical(metrics.fill) }} />
        </div>
      </div>
    </div>
  );
}

/**
 * The clear space between the maintenance strip's bottom and the prompt's top while the strip shows, in logical pixels.
 */
export const PROMPT_GAP = 8;

/**
 * The prompt's outer width in logical pixels: the timer bar's width inside the HUD frame's padding and insets.
 */
export const PROMPT_FRAME_WIDTH = PROMPT_BAR_WIDTH + 2 * KIT_METRICS.hud.padding + FRAMES.hud.insets.left + FRAMES.hud.insets.right;

/**
 * Where the prompt stands on an area: centred at the top of the screen, which the layout already holds clear of the
 * player card and the chat panel on a small area. It stands in the maintenance strip's place, and below the strip,
 * `PROMPT_GAP` clear of it, while the strip shows, so a shutdown countdown is never covered.
 *
 * @param area The area.
 * @param stripShowing Whether the maintenance strip is showing.
 * @returns The prompt's left and top and its width, in logical pixels.
 */
export function promptPlace(area: HudArea, stripShowing: boolean): { readonly left: number; readonly top: number; readonly width: number } {
  const strip = slotRect("maintenance", area);
  const top = stripShowing ? strip.top + strip.height + PROMPT_GAP : strip.top;
  return { left: strip.left + Math.floor((strip.width - PROMPT_FRAME_WIDTH) / 2), top, width: PROMPT_FRAME_WIDTH };
}

/**
 * The page's clock, read again every `TIMER_REDRAW_MS` while mounted.
 *
 * @param read The clock.
 * @returns The latest reading.
 */
function useClock(read: () => number): number {
  const [now, setNow] = useState(read);
  useEffect(() => {
    setNow(read());
    const handle = setInterval(() => setNow(read()), TIMER_REDRAW_MS);
    return () => clearInterval(handle);
  }, [read]);
  return now;
}

/**
 * What the prompt reads of the player's flag and how it asks for a switch: the HUD's source, the request a
 * `pvpFlagToggle` goes out through (the flag panel's own, answering whether it went), and where a line for the player
 * goes (the message line).
 */
export interface PromptHud {
  readonly source: HudSource;
  readonly request: (wanted: number) => boolean;
  readonly notice: (line: string) => void;
}

/**
 * The prompt for one invitation.
 *
 * @param props The invitation, how many more `waiting`, the `area`, the `party` it answers through, whether the
 *   maintenance strip is showing, and the `hud` it reads the player's flag from (a plain Accept without one).
 * @returns The prompt in its slot.
 */
function Prompt({ prompt, waiting, area, party, stripShowing, hud }: { readonly prompt: PartyPrompt; readonly waiting: number; readonly area: HudArea; readonly party: PartySource; readonly stripShowing: boolean; readonly hud: PromptHud | undefined }): ReactElement {
  const now = useClock(party.now);
  const facts = useSyncExternalStore(hud?.source.subscribe ?? NO_SUBSCRIBE, hud?.source.getSnapshot ?? NO_FACTS, hud?.source.getSnapshot ?? NO_FACTS);
  const flagView = hud === undefined || facts === null ? PLAIN_VIEW : promptFlagView(prompt.flag, { ...facts, nowMs: now });
  const waitingSwitch = useRef<(() => void) | null>(null);
  const [switching, setSwitching] = useState(false);
  const noticeRef = useRef(hud?.notice);
  noticeRef.current = hud?.notice;
  const inFlight = useRef(false);
  useEffect(
    () => () => {
      waitingSwitch.current?.();
      if (inFlight.current) {
        noticeRef.current?.(say("party.prompt.switchEnded"));
      }
    },
    [],
  );
  const ended = (end: SwitchEnd): void => {
    inFlight.current = false;
    waitingSwitch.current = null;
    setSwitching(false);
    if (end.kind === "unsent") {
      hud?.notice(say("party.prompt.switchNotSent"));
    } else if (end.kind === "refused") {
      hud?.notice(flagRefusalLine(end.note, prompt.flag) ?? say("party.prompt.switchStopped"));
    }
  };
  const accept = (): void => {
    if (flagView.accept === "switch" && hud !== undefined) {
      if (inFlight.current) {
        return;
      }
      inFlight.current = true;
      setSwitching(true);
      waitingSwitch.current = switchAndAccept({ subscribe: hud.source.subscribe, getSnapshot: hud.source.getSnapshot, request: hud.request }, (invite, accepted) => party.answer(invite, accepted), prompt.invite, prompt.flag, ended);
      return;
    }
    party.answer(prompt.invite, true);
  };
  const slot = promptPlace(area, stripShowing);
  const name = `${prompt.firstName} ${prompt.familyName}`.trim();
  return (
    <div className="ot-hud-slot ot-party-prompt" data-hud="invitation" data-invite={prompt.invite} role="group" aria-label={say("party.prompt.label", { name })} style={{ left: logical(slot.left), top: logical(slot.top), width: logical(slot.width) }}>
      <HudFrame width={PROMPT_FRAME_WIDTH}>
        <div className="ot-party-prompt__body">
          <div className="ot-party-prompt__title" data-party="title">
            <Glyph name="users" />
            <span>{say("party.prompt.title")}</span>
          </div>
          <div className="ot-party-prompt__from" data-party="from" aria-live="polite">
            {say("party.prompt.from", { name, count: String(prompt.members), max: String(PARTY_SIZE) })}
          </div>
          <TimerBar prompt={prompt} now={now} />
          {flagView.line === null ? null : (
            <div className="ot-party-prompt__flag" data-party="flag" data-warning={flagView.warning ? "true" : undefined} role="status">
              {flagView.line}
            </div>
          )}
          <div className={flagView.accept === "accept" || flagView.accept === "none" ? "ot-party-prompt__buttons" : "ot-party-prompt__buttons ot-party-prompt__buttons--switch"} data-party="buttons" onMouseDown={(event) => event.preventDefault()}>
            <Button fullWidth disabled={prompt.answering} onClick={() => party.answer(prompt.invite, false)}>
              {say("party.prompt.decline")}
            </Button>
            {flagView.accept === "none" ? null : (
              <Button fullWidth variant="primary" disabled={prompt.answering || switching || flagView.accept === "wait"} onClick={accept}>
                {say(flagView.accept === "accept" ? "party.prompt.accept" : "party.prompt.switchAccept")}
              </Button>
            )}
          </div>
          {waiting > 0 ? (
            <div className="ot-party-prompt__waiting" data-party="waiting">
              {say("party.prompt.waiting", { count: String(waiting) })}
            </div>
          ) : null}
        </div>
      </HudFrame>
    </div>
  );
}

/**
 * The flag part of a prompt that reads no HUD: a plain Accept.
 */
const PLAIN_VIEW = { line: null, warning: false, accept: "accept" } as const;
/**
 * A subscription to nothing, for a prompt without a HUD.
 *
 * @returns A function that ends it.
 */
const NO_SUBSCRIBE = (): (() => void) => () => undefined;
/**
 * The HUD facts of a prompt without a HUD: none.
 *
 * @returns Null.
 */
const NO_FACTS = (): null => null;

const NO_WARNINGS: Pick<MaintenanceWarnings, "subscribe" | "getSnapshot"> = { subscribe: () => () => undefined, getSnapshot: () => ({ toast: null, strip: null }) };

/**
 * The invitation prompt: the invitation the store shows, if any, below the maintenance strip while it shows.
 *
 * @param props The `area` it is laid out on, the `party` it reads, the page's `warnings` that say whether the strip shows,
 *   and the `hud` it reads the player's flag from and asks a switch through.
 * @returns The prompt, or null while no invitation shows.
 */
export function InvitationPrompt({ area, party, warnings, hud }: { readonly area: HudArea; readonly party: PartySource; readonly warnings?: Pick<MaintenanceWarnings, "subscribe" | "getSnapshot"> | undefined; readonly hud?: PromptHud | undefined }): ReactElement | null {
  const snapshot = useSyncExternalStore(party.subscribe, party.getSnapshot, party.getSnapshot);
  const source = warnings ?? NO_WARNINGS;
  const stripShowing = useSyncExternalStore(source.subscribe, () => source.getSnapshot().strip !== null, () => source.getSnapshot().strip !== null);
  return snapshot.prompt === null ? null : <Prompt key={snapshot.prompt.invite} prompt={snapshot.prompt} waiting={snapshot.waiting} area={area} party={party} stripShowing={stripShowing} hud={hud} />;
}
